// Why the mapping modal's Emissions tab can (or can't) show the Project series, and what to tell the
// user when it can't.
//
// Kept out of EmissionsPanel.vue so the four states are decidable — and testable — from plain data:
// the material's breakdown row, whether the breakdown has any rows at all, and whether the material
// is included in the calculation. The panel used to hide the Project/EPD switch whenever the project
// side didn't resolve and present EPD-per-declared-unit numbers in its place with no explanation
// (RTLCA-5420); now the switch stays, disabled, next to the reason.
import type { HotspotRow } from "@/features/Dashboard/composables/hotspotBreakdown";

export type ProjectSeriesState = "available" | "excluded" | "loading" | "zero" | "unresolved";

export interface ProjectSeriesAvailability {
  state: ProjectSeriesState;
  /** True only for `"available"` — drives whether the Project tab can be selected. */
  available: boolean;
  /** What to show next to the disabled switch. Empty string when the Project series is available. */
  reason: string;
}

/**
 * True when the row has anything at all to plot.
 *
 * Deliberately counts every stage the chart draws, D and B6 included, even though `sumMaterialTotal`
 * leaves those two out of the printed Total. A material whose only contribution is a negative module
 * D credit *does* have a project figure, and the old `total > 0` test silently dropped it to the EPD
 * view — which is one of the defects this replaces.
 */
const hasAnyStageValue = (rawStages: Record<string, number>): boolean =>
  Object.values(rawStages).some((value) => (value ?? 0) !== 0);

export function resolveProjectSeriesAvailability(input: {
  /** The material's row from `buildProjectMaterialRows`, or null when it isn't in the breakdown. */
  row: HotspotRow | null;
  /** False when the (unfiltered) project breakdown has produced no rows yet. */
  breakdownHasRows: boolean;
  /** `SpeckleMaterial.includeMapping` — the API's `bp.Materials.Where(x => x.IncludeMapping)`. */
  includeMapping: boolean;
}): ProjectSeriesAvailability {
  const { row, breakdownHasRows, includeMapping } = input;

  if (row && hasAnyStageValue(row.rawStages)) return { state: "available", available: true, reason: "" };

  // Checked before the loading state on purpose: an excluded material is never going to appear in
  // the breakdown, so "still loading" would be a promise we can't keep.
  if (!includeMapping) {
    return {
      state: "excluded",
      available: false,
      reason: "This material is excluded from the calculation, so it has no project-level emissions.",
    };
  }

  if (!breakdownHasRows) {
    return { state: "loading", available: false, reason: "Project totals are still loading." };
  }

  if (row) {
    return {
      state: "zero",
      available: false,
      reason: "This material contributes nothing to the project total.",
    };
  }

  return {
    state: "unresolved",
    available: false,
    reason: "Project-level totals couldn't be resolved for this material.",
  };
}
